import classNames from 'classnames';
import React, { CSSProperties, ElementType, forwardRef, memo, ReactNode, Ref, useMemo } from 'react';
import pattern from '../assets/images/bg-pattern.png';
import { usePayStack, useRouteLink } from '../helpers/hooks';
import Icon, { IconProps } from './Icon';
import ModalLink from './Modal/ModalLink';
import { createSocialSharingLink } from './SocialSharing';
import Text from './Text';
import { Alert } from '../helpers/utils';
import { useHistory } from 'react-router-dom';

const IMAGES: { [key: string]: any } = {
  pattern,
};

const ignoreClassNames = ['paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight'];

const scaleStyles = (obj: { [key: string]: number | string }, scale = 2) => {
  const keys = Object.keys(obj);
  const newObj: { [key: string]: number | string } = {};
  keys.forEach(key => {
    const value = obj[key];
    if (typeof value === 'number' && !ignoreClassNames.includes(key)) {
      newObj[key] = value * scale;
    } else {
      newObj[key] = value;
    }
  });
  return newObj;
};

export type FlatCardText = CSSProperties & {
  text: ReactNode;
};

export type FlatCardData = {
  title?: FlatCardText;
  subTitle?: FlatCardText;
  textRight?: FlatCardText;
  feel?: {
    [key: string]: any;
  };
  iconLeft?: IconProps;
  iconRight?: IconProps;
};

type FlatCardProps = FlatCardData & {
  link?: string;
  linkData?: object;
  backgroundImage?: string;
  as?: 'Link' | 'a' | 'button' | 'div' | string;
  containerClassName?: string;
  titleClassName?: string;
  subTitleClassName?: string;
  imageLeft?: string;
  bordered?: boolean;
  left?: ReactNode;
  height?: string;
  [key: string]: any;
};

const FlatCard = (
  {
    title: { text: title, ...titleStyle } = { text: '' },
    subTitle: { text: subTitle, ...subTitleStyle } = { text: '' },
    textRight: { text: textRight, ...textRightStyle } = { text: '' },
    feel: containerStyle = {},
    iconLeft,
    iconRight,
    link = '',
    linkData = {},
    backgroundImage = '',
    as = 'Link',
    containerClassName = '',
    titleClassName = '',
    subTitleClassName = '',
    imageLeft = '',
    bordered = true,
    left = null,
    height = '',
    ...restProps
  }: FlatCardProps,
  ref: Ref<any>,
) => {
  const linkTo = useRouteLink();
  const history = useHistory();
  const { pay, loading: paymentLoading } = usePayStack();
  const [Container, asProps] = useMemo(() => {
    const to = linkTo(link, linkData);
    if (window.location.pathname === '/settings/p/selfhelp/p/settings_repairtransaction') {
      Alert.info('This action has been temporarily disabled. Kindly contact support or call us to help out with this');
      history.push('/settings/p/selfhelp');
    }

    let dContainer: ElementType;
    const dAsProps = { ...restProps };
    if (to && to.pathname) {
      dContainer = ModalLink;
      dAsProps.to = to;
    } else if (to && to.state && to.state.routeId === 'Pay') {
      dContainer = 'button';
      dAsProps.type = 'button';
      dAsProps.onClick = pay(to.state);
      dAsProps.loading = paymentLoading;
    } else if (to?.state?.routeId === 'ShareAction') {
      dContainer = createSocialSharingLink(to.state);
    } else {
      switch (as) {
        case 'a':
          dContainer = 'a';
          break;
        case 'Link':
          dContainer = dAsProps.to ? ModalLink : 'div';
          break;
        case 'button':
          dContainer = 'button';
          dAsProps.type = 'button';
          break;
        default:
          dContainer = 'div';
      }
    }
    return [dContainer, dAsProps];
  }, [linkTo, history, link, linkData, restProps, pay, paymentLoading, as]);

  const hidePercentages = (str: any) => {
    if (window.location.pathname !== '/savings/safelock/p/learn_safelock' && title !== 'Earn interest upfront!') {
      return str;
    }
    const arr = str.split(' ');
    for (let i = arr.length; i >= 0; i--) {
      if (arr[i]?.toString().includes('%')) {
        arr[i] = '';
        break;
      }
      if (arr[i] === 'p.a') {
        arr[i] = '';
      }
    }

    return arr.join(' ');
  };

  return (
    <Container
      ref={ref}
      className={classNames(
        'flex flex-shrink-0 items-center',
        { 'rounded-t rounded-br border': bordered },
        containerClassName,
      )}
      style={{
        ...scaleStyles(containerStyle),
        ...(backgroundImage
          ? {
              backgroundImage: `url(${IMAGES[backgroundImage] || backgroundImage}`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: height ? 'left' : 'right',
              height: height,
            }
          : {}),
        ...scaleStyles(restProps),
      }}
      {...asProps}
    >
      {left}
      {iconLeft && <Icon {...iconLeft} />}
      {imageLeft && <img src={imageLeft} alt='' className='rounded-full w-8 h-8 mr-4' />}
      <div className='flex flex-col mr-2 w-full'>
        {title && (
          <Text className={classNames('text-left', titleClassName)} {...titleStyle}>
            {title}
          </Text>
        )}
        {subTitle && (
          <Text className={classNames('text-left', subTitleClassName)} {...subTitleStyle}>
            {hidePercentages(subTitle)}
          </Text>
        )}
      </div>
      <div className='flex flex-col flex-1 self-start'>
        {textRight && (
          <Text className={classNames('text-right', titleClassName)} {...textRightStyle}>
            {textRight}
          </Text>
        )}
      </div>
      {iconRight && <Icon type='ion' {...iconRight} />}
    </Container>
  );
};

export default memo(forwardRef<any, FlatCardProps>(FlatCard));
